import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Award,
  BookOpen,
  CalendarClock,
  Check,
  CheckCircle2,
  Flame,
  Gauge,
  LockKeyhole,
  Sparkles,
  Target,
  Trophy,
  Zap,
} from 'lucide-react';
import { curriculum, phases } from '../content/curriculum';
import { useProgress } from '../app/ProgressContext';
import { Heatmap } from '../components/Heatmap';
import { ProgressRing } from '../components/ProgressRing';

const badges = [
  { id: 'first-step', name: 'First Step', hint: 'Complete day 1', icon: Sparkles, target: 1, metric: 'days' },
  { id: 'week-warrior', name: 'Week Warrior', hint: 'Reach a 7-day streak', icon: Flame, target: 7, metric: 'streak' },
  { id: 'dsa-foundations', name: 'Foundation Set', hint: 'Complete phase 1', icon: BookOpen, target: 18, metric: 'days' },
  { id: 'algorithm-architect', name: 'Algorithm Architect', hint: 'Complete phase 2', icon: Target, target: 38, metric: 'days' },
  { id: 'systems-thinker', name: 'Systems Thinker', hint: 'Complete phase 3', icon: Award, target: 56, metric: 'days' },
  { id: 'interview-ready', name: 'Interview Ready', hint: 'Complete all 60 days', icon: Trophy, target: 60, metric: 'days' },
];

function localDay(date: Date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function journeyMessage(completed: number) {
  if (completed === 0) return ['Your offer ETA starts today.', 'One completed lesson turns this from a plan into a trajectory.'];
  if (completed < 18) return ['Foundation is becoming momentum.', 'Keep building the core patterns that make harder problems feel familiar.'];
  if (completed < 38) return ['Your pattern library is compounding.', 'You are moving from isolated solutions to reusable interview instincts.'];
  if (completed < 56) return ['You are thinking in systems now.', 'Connect every architecture choice to scale, reliability, and product requirements.'];
  if (completed < 60) return ['Final interview reps. Finish the route.', 'The knowledge is built; now sharpen retrieval, communication, and calm execution.'];
  return ['Roadmap complete. The offer pipeline is next.', 'Keep revising weak signals and use the interview tracker to convert preparation into outcomes.'];
}

export function ProgressPage() {
  const { progress } = useProgress();
  const completed = progress.stats.completedDays;
  const percent = completed / 60 * 100;
  const remaining = Math.max(0, 60 - completed);
  const activeDates = [...new Set(progress.activity)].sort();
  const elapsed = activeDates.length
    ? Math.max(1, Math.ceil((Date.now() - new Date(`${activeDates[0]}T12:00:00`).getTime()) / 86_400_000) + 1)
    : 1;
  const pace = completed ? Math.min(2, Math.max(.25, completed / elapsed)) : 1;
  const calendarDays = Math.ceil(remaining / pace);
  const projectedFinish = new Date();
  projectedFinish.setDate(projectedFinish.getDate() + Math.max(0, calendarDays - 1));
  const finishLabel = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).format(projectedFinish);
  const nextLesson = curriculum.find((lesson) => progress.days[lesson.day]?.status !== 'completed');
  const [headline, supporting] = journeyMessage(completed);
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - 6);
  const thisWeek = new Set(progress.activity.filter((date) => date >= localDay(weekStart))).size;
  const nextBadge = badges.find((badge) => !progress.badges.includes(badge.id)) ?? badges[badges.length - 1];
  const nextBadgeValue = nextBadge.metric === 'streak' ? progress.stats.currentStreak : completed;
  const nextBadgeProgress = progress.badges.includes(nextBadge.id) ? 100 : Math.min(100, nextBadgeValue / nextBadge.target * 100);
  const levelProgress = progress.stats.xp % 500 / 500 * 100;

  const phaseViews = phases.map((phase) => {
    const days = curriculum.filter((day) => day.phaseNumber === phase.number);
    const done = days.filter((day) => progress.days[day.day]?.status === 'completed').length;
    const next = days.find((day) => progress.days[day.day]?.status !== 'completed');
    return { ...phase, days, done, next, percentage: Math.round(done / days.length * 100) };
  });

  return (
    <div className="page progress-page-v2">
      <header className="page-heading split progress-heading-v2">
        <div><span className="eyebrow">YOUR OFFER ETA</span><h1>Momentum, mapped.</h1><p>See the distance travelled, the pace you’re building, and the next move that matters.</p></div>
        <div className="eta-date-card"><CalendarClock /><span><small>PROJECTED FINISH</small><strong>{finishLabel}</strong><em>{completed ? `At ${pace.toFixed(1)} lessons per day` : 'Start today · one lesson per day'}</em></span></div>
      </header>

      <motion.section className="eta-progress-hero" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
        <div className="eta-hero-story">
          <span className="eta-live"><i /> LIVE JOURNEY PROJECTION</span>
          <h2>{headline}</h2>
          <p>{supporting}</p>
          <div className="sixty-day-route">
            <div className="route-labels"><span>DAY 01</span><b>DAY {String(Math.min(60, completed + 1)).padStart(2, '0')}</b><span>OFFER READY</span></div>
            <div className="route-segments">{Array.from({ length: 60 }, (_, index) => {
              const day = index + 1;
              const phase = day <= 18 ? 1 : day <= 38 ? 2 : day <= 56 ? 3 : 4;
              return <i key={day} className={`${day <= completed ? `done phase-${phase}` : ''} ${day === completed + 1 ? 'current' : ''}`} title={`Day ${day}`} />;
            })}</div>
          </div>
          <div className="eta-hero-actions">
            <Link className="button primary" to={nextLesson ? `/day/${nextLesson.day}` : '/interviews'}>{nextLesson ? `Continue Day ${nextLesson.day}` : 'Open interview tracker'} <ArrowRight /></Link>
            <span><Zap /> {remaining} focused lessons between you and the finish line</span>
          </div>
        </div>

        <aside className="eta-score-panel">
          <div className="eta-ring"><ProgressRing value={percent} size={178} label="route complete" /><span className="ring-glow" /></div>
          <div className="next-milestone">
            <span><nextBadge.icon /></span>
            <div><small>NEXT MILESTONE</small><strong>{nextBadge.name}</strong><p>{nextBadge.hint}</p></div>
          </div>
          <div className="milestone-meter"><i style={{ width: `${nextBadgeProgress}%` }} /></div>
          <em>{Math.min(nextBadgeValue, nextBadge.target)} / {nextBadge.target}</em>
        </aside>
      </motion.section>

      <section className="progress-kpis-v2">
        <motion.article initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .05 }}><span><CheckCircle2 /></span><div><small>DAYS COMPLETE</small><strong>{completed}<em>/ 60</em></strong><p>{remaining} lessons remaining</p></div></motion.article>
        <motion.article initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .1 }}><span><Flame /></span><div><small>CURRENT STREAK</small><strong>{progress.stats.currentStreak}<em> days</em></strong><p>Best: {progress.stats.longestStreak} days</p></div></motion.article>
        <motion.article initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .15 }}><span><Gauge /></span><div><small>EXPERIENCE</small><strong>{progress.stats.xp.toLocaleString()}<em> XP</em></strong><p>Level {progress.stats.level} · {Math.round(levelProgress)}% to next</p></div></motion.article>
        <motion.article initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .2 }}><span><Trophy /></span><div><small>ACHIEVEMENTS</small><strong>{progress.badges.length}<em>/ 6</em></strong><p>{6 - progress.badges.length} badges still to unlock</p></div></motion.article>
      </section>

      <section className="phase-journey-v2">
        <header><div><span className="eyebrow">THE 60-DAY ROUTE</span><h2>Four chapters to interview-ready</h2><p>Each phase compounds into the next. Finish the current chapter before chasing more breadth.</p></div><span className="route-total"><b>{completed}</b><i>/60</i><small>DAYS BANKED</small></span></header>
        <div className="phase-progress-grid-v2">{phaseViews.map((phase) => (
          <article key={phase.number} className={`${phase.next?.phaseNumber === nextLesson?.phaseNumber ? 'active' : ''} ${phase.percentage === 100 ? 'complete' : ''}`} style={{ '--phase': phase.color } as React.CSSProperties}>
            <header><span>0{phase.number}</span><em>{phase.range}</em>{phase.percentage === 100 && <i><Check /></i>}</header>
            <h3>{phase.name}</h3>
            <p>{phase.next ? `Next: ${phase.next.title}` : 'Chapter complete — knowledge banked.'}</p>
            <div className="phase-meter-v2"><i style={{ width: `${phase.percentage}%` }} /></div>
            <div className="phase-numbers"><span><b>{phase.done}</b> / {phase.days.length} days</span><strong>{phase.percentage}%</strong></div>
            {phase.next ? <Link to={`/day/${phase.next.day}`}>Open next lesson <ArrowRight /></Link> : <span className="phase-complete-label"><CheckCircle2 /> Complete</span>}
          </article>
        ))}</div>
      </section>

      <section className="progress-lower-v2">
        <article className="momentum-map-v2">
          <header><div><span className="eyebrow">CONSISTENCY SIGNAL</span><h2>Your momentum map</h2><p>Small marks become interview instinct when they stay close together.</p></div><span className="week-score"><b>{thisWeek}</b><small>active days<br />this week</small></span></header>
          <div className="heatmap-frame"><div className="heatmap-months"><span>20 WEEKS AGO</span><span>TODAY</span></div><Heatmap activity={progress.activity} weeks={20} /></div>
          <div className="momentum-footer"><span><Flame /> {progress.stats.currentStreak ? `Your ${progress.stats.currentStreak}-day chain is alive.` : 'Your next streak starts with one completed task.'}</span><em><i /> Practice day</em></div>
        </article>

        <article className="achievement-vault-v2">
          <header><div><span className="eyebrow">MILESTONE VAULT</span><h2>Proof you can retrieve</h2></div><span>{progress.badges.length} / {badges.length} UNLOCKED</span></header>
          <div className="achievement-list-v2">{badges.map(({ id, name, hint, icon: Icon }) => {
            const earned = progress.badges.includes(id);
            return <div className={earned ? 'earned' : ''} key={id}><span>{earned ? <Icon /> : <LockKeyhole />}</span><div><strong>{name}</strong><small>{earned ? 'Earned and banked' : hint}</small></div><i>{earned ? <Check /> : 'LOCKED'}</i></div>;
          })}</div>
        </article>
      </section>
    </div>
  );
}
